// Who is in a channel, and adding to it (WF-FIX 2026-10-26; CHAT-DE 2026-10-27, Slack's shape):
// - `MemberStack` (the header): the members' faces, digital employees first (each an agent tile),
//   and the count; it opens Channel details › Members. The agents' wake rule shows as a small
//   chip only when it is not the default ("silent", "every topic message"); it is always in the
//   stack's name.
// - `AddPeopleButton` (beside the stack): one search across the company's people and DEs not in
//   the channel, DEs with their badge (a paused one shown, not pickable; retired ones left out).
//   `channels.addMember` checks again; any poster may add on a public channel and any member on
//   a private one (the list row's `can_invite`). `/invite` opens the same list in a dialog.
// - `SearchPicker` / `SearchList`: the small search-and-pick list this, the DE profile's "Add to
//   channel" and the sidebar use.
// The agents' wake rule and standing instructions are Channel details › Settings now
// (`AgentRulesRow`, channel-settings.tsx).
import {
  AvatarTile,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  toast,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlusIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import { problemText } from "../ops-parts";
import { WAKE_CHIP } from "./channel-settings";
import type { ChatMember } from "./chat-model";
import { isDm } from "./chat-model";
import { type Channel, channelsQuery, chatKeys, membersQuery, rulesQuery } from "./chat-queries";
import type { ChatContext } from "./use-chat";

export type PickerItem = {
  id: string;
  text: string;
  render: ReactNode;
  /** WF-FIX (2026-10-26): shown but not pickable, with this short reason ("Paused"). */
  disabledReason?: string;
};

type ListStatus = "ready" | "loading" | "error";

/**
 * A search box over `items`, each a button; picking one calls `onPick`. `empty` says why there
 * is nothing to pick; `status` holds the items back while what they derive from is loading or
 * failed (with `onRetry`).
 */
export function SearchList({
  label,
  items,
  empty,
  placeholder = "Search…",
  onPick,
  status = "ready",
  onRetry,
}: {
  label: string;
  items: readonly PickerItem[];
  empty: string;
  placeholder?: string;
  onPick: (id: string) => void;
  status?: ListStatus;
  onRetry?: (() => void) | undefined;
}) {
  const [q, setQ] = useState("");
  const shown = items.filter((i) => i.text.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <>
      <Input
        aria-label={`${label}: search`}
        className="h-8"
        placeholder={placeholder}
        value={q}
        autoFocus
        onChange={(e) => setQ(e.target.value)}
      />
      {status === "loading" ? (
        <p
          role="status"
          aria-label={`${label}: loading`}
          className="m-0 px-2 py-1.5 text-sm text-muted-foreground"
        >
          Loading…
        </p>
      ) : status === "error" ? (
        <div
          role="alert"
          className="flex items-center justify-between gap-2 px-2 py-1.5 text-sm text-muted-foreground"
        >
          <span>Could not load the list.</span>
          {onRetry ? (
            <Button type="button" variant="secondary" size="xs" onClick={onRetry}>
              Retry
            </Button>
          ) : null}
        </div>
      ) : (
        <ul aria-label={label} className="m-0 flex max-h-64 list-none flex-col overflow-y-auto p-0">
          {shown.map((i) => (
            <li key={i.id}>
              <button
                type="button"
                data-pick={i.id}
                disabled={i.disabledReason !== undefined}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent"
                onClick={() => {
                  if (i.disabledReason !== undefined) return;
                  setQ("");
                  onPick(i.id);
                }}
              >
                {i.render}
                {i.disabledReason !== undefined ? (
                  <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                    {i.disabledReason}
                  </span>
                ) : null}
              </button>
            </li>
          ))}
          {!shown.length ? (
            <li className="px-2 py-1.5 text-sm text-muted-foreground">
              {items.length ? "No match." : empty}
            </li>
          ) : null}
        </ul>
      )}
    </>
  );
}

/**
 * A visible button opening a small popover with a `SearchList`. Picking one calls `onPick` and
 * closes it.
 */
export function SearchPicker({
  label,
  children,
  items,
  empty,
  placeholder = "Search…",
  onPick,
  disabled = false,
  variant = "ghost",
  size = "xs",
  className,
  onOpenChange,
  status = "ready",
  onRetry,
}: {
  /** Told when the picker opens or closes (a caller that loads its items only once opened). */
  onOpenChange?: (open: boolean) => void;
  /**
   * WF-FIX (2026-10-26): `items` are offered only once every read behind them succeeded;
   * meanwhile "Loading…", or the failure with `onRetry`.
   */
  status?: ListStatus;
  onRetry?: () => void;
  /** The trigger's accessible name and the popover's heading. */
  label: string;
  /** The trigger's content (icon and text). */
  children: ReactNode;
  items: readonly PickerItem[];
  empty: string;
  placeholder?: string;
  onPick: (id: string) => void;
  disabled?: boolean;
  variant?: "ghost" | "secondary" | "outline";
  size?: "xs" | "sm" | "icon-sm" | "icon-xs";
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        onOpenChange?.(o);
      }}
    >
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant={variant}
            size={size}
            aria-label={label}
            title={label}
            disabled={disabled}
            {...(className ? { className } : {})}
          />
        }
      >
        {children}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 gap-2 p-2" data-slot="search-picker">
        <SearchList
          label={label}
          items={items}
          empty={empty}
          placeholder={placeholder}
          status={status}
          onRetry={onRetry}
          onPick={(id) => {
            setOpen(false);
            onPick(id);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

/** A DE's face, name (without a "DE · " label) and the DE badge. */
export function DeLabel({ name }: { name: string }) {
  return (
    <>
      <AvatarTile name={workerName(name)} kind="agent" size="xs" aria-hidden="true" />
      <span className="min-w-0 truncate">{workerName(name)}</span>
      <WorkerBadge kind="de" size="sm" />
    </>
  );
}

/** A person's face and name. */
function PersonLabel({ name }: { name: string }) {
  return (
    <>
      <AvatarTile name={name} kind="person" size="xs" aria-hidden="true" />
      <span className="min-w-0 truncate">{name}</span>
    </>
  );
}

/**
 * WF-FIX (2026-10-26): whether the caller may add members to this channel — the list row's
 * `can_invite` (the API's `channelAccess(...).invite`), from the sidebar's cached channel list.
 */
export function useCanInvite(chat: ChatContext, channel: Pick<Channel, "id" | "archived_at">) {
  const all = useQuery({
    ...channelsQuery(chat.api, chat.co),
    enabled: chat.meId !== null,
    retry: false,
  });
  if (chat.meId === null || chat.readOnly || channel.archived_at) return false;
  return all.data?.data.find((c) => c.id === channel.id)?.can_invite ?? false;
}

/**
 * WF-FIX (2026-10-26): whether the caller manages this channel — the list row's `can_manage`
 * (the API's `access.manage`, which counts the space's owners too); `fallback` (the dialog's
 * company-role / channel-admin guess) only while the row is not there.
 */
export function useManagesChannel(
  chat: ChatContext,
  channel: Pick<Channel, "id" | "archived_at">,
  fallback: boolean,
): boolean {
  const all = useQuery({
    ...channelsQuery(chat.api, chat.co),
    enabled: chat.meId !== null,
    retry: false,
  });
  if (chat.meId === null || chat.readOnly || channel.archived_at) return false;
  return all.data?.data.find((c) => c.id === channel.id)?.can_manage ?? fallback;
}

/** Refreshes what a channel member change touches: the rows, the lists, the @ menu's readiness. */
export function useMemberRefresh(chat: ChatContext, channelId: string) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: chatKeys.members(chat.co, channelId) }),
      queryClient.invalidateQueries({ queryKey: chatKeys.channels(chat.co) }),
      queryClient.invalidateQueries({ queryKey: ["company", chat.co, "agent-readiness"] }),
    ]);
}

/** The channel's members as the company's index names them: DEs first, then people. */
export function useChannelMembers(chat: ChatContext, channel: Pick<Channel, "id">, enabled = true) {
  const members = useQuery({
    ...membersQuery(chat.api, chat.co, channel.id),
    retry: false,
    enabled,
  });
  const rows = members.data?.data ?? [];
  const ids = new Set(rows.map((m) => m.membership_id));
  const known = rows.flatMap((m) => {
    const who = chat.members.get(m.membership_id);
    return who ? [who] : [];
  });
  const des = known.filter((m) => m.kind === "de");
  const people = known.filter((m) => m.kind !== "de");
  return { members, ids, des, people };
}

/** Adds one membership to the channel, with the toast that names it. */
export function useAddMember(chat: ChatContext, channel: Pick<Channel, "id" | "name">) {
  const refresh = useMemberRefresh(chat, channel.id);
  return useMutation({
    mutationFn: (membership_id: string) =>
      chat.api.client.channels.addMember({
        params: { co: chat.co, id: channel.id },
        body: { membership_id },
      }),
    onSuccess: (_r, id) => {
      void refresh();
      const who = chat.members.get(id);
      toast.add({
        title: `Added ${who ? (who.kind === "de" ? workerName(who.name) : who.name) : "them"} to #${channel.name}`,
        type: "success",
      });
    },
    onError: (e) =>
      toast.add({
        title: "Could not add them",
        description: problemText(e, e.message),
        type: "error",
      }),
  });
}

const byName = (a: ChatMember, b: ChatMember) =>
  workerName(a.name).localeCompare(workerName(b.name));

/**
 * The "Add people" list: the company's people and DEs not in the channel, offered only once the
 * channel's members and every DE page are read (an unread list is not empty). People first,
 * then DEs (badged); retired DEs left out, a paused one shown with its reason.
 */
export function useAddCandidates(chat: ChatContext, channel: Pick<Channel, "id">) {
  const { members, ids } = useChannelMembers(chat, channel);
  const all = [...chat.members.values()].filter((m) => !ids.has(m.id));
  const people = all.filter((m) => m.kind === "person").sort(byName);
  const des = all.filter((m) => m.kind === "de" && m.deStatus !== "retired").sort(byName);
  const items: PickerItem[] = [
    ...people.map((m) => ({ id: m.id, text: m.name, render: <PersonLabel name={m.name} /> })),
    ...des.map((m) => ({
      id: m.id,
      text: workerName(m.name),
      render: <DeLabel name={m.name} />,
      ...(m.deStatus === "paused" || m.deStatus === "not_deployed"
        ? { disabledReason: m.deStatus === "paused" ? "Paused" : "Not deployed" }
        : {}),
    })),
  ];
  // Every read behind the list: the members, every DE page and every people page.
  const status: ListStatus =
    members.isError || chat.deDirectory.failed || chat.peopleDirectory.failed
      ? "error"
      : members.isSuccess && chat.deDirectory.complete && chat.peopleDirectory.complete
        ? "ready"
        : "loading";
  const retry = () => {
    if (members.isError) void members.refetch();
    if (chat.deDirectory.failed) chat.deDirectory.retry();
    if (chat.peopleDirectory.failed) chat.peopleDirectory.retry();
  };
  return { items, status, retry };
}

/** "Add people": one search across people and DEs, beside the header's member stack. */
export function AddPeopleButton({
  chat,
  channel,
  variant = "ghost",
}: {
  chat: ChatContext;
  channel: Channel;
  /** The header's icon, or a labelled button (Members). */
  variant?: "ghost" | "secondary";
}) {
  const canInvite = useCanInvite(chat, channel);
  const { items, status, retry } = useAddCandidates(chat, channel);
  const add = useAddMember(chat, channel);
  if (!canInvite || isDm(channel)) return null;
  const icon = variant === "ghost";
  return (
    <SearchPicker
      label="Add people"
      variant={variant}
      size={icon ? "icon-sm" : "xs"}
      placeholder="Search people and digital employees"
      empty="Everyone is in this channel."
      disabled={add.isPending}
      status={status}
      onRetry={retry}
      items={items}
      onPick={(id) => add.mutate(id)}
      {...(icon ? { className: "text-secondary" } : {})}
    >
      <UserPlusIcon {...(icon ? {} : { "data-icon": "inline-start" })} />
      {icon ? null : "Add people"}
    </SearchPicker>
  );
}

/** `/invite`: the same list in a dialog (the composer's slash command opens it). */
export function InviteDialog({
  chat,
  channel,
  onClose,
}: {
  chat: ChatContext;
  channel: Channel;
  onClose: () => void;
}) {
  const { items, status, retry } = useAddCandidates(chat, channel);
  const add = useAddMember(chat, channel);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm" data-dialog="invite">
        <DialogHeader>
          <DialogTitle>Add people to #{channel.name}</DialogTitle>
          <DialogDescription>People and digital employees.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <SearchList
            label="Add people"
            placeholder="Search people and digital employees"
            empty="Everyone is in this channel."
            items={items}
            status={status}
            onRetry={retry}
            onPick={(id) => add.mutate(id, { onSuccess: onClose })}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The header's member stack (CHAT-DE): up to four faces, DEs first, and the member count; it
 * opens Channel details › Members. With DEs here, the agents' rule is in its name, and shows as
 * a chip when it is not the default. A failed DE page says so with Retry (never "no DE here").
 */
export function MemberStack({
  chat,
  channel,
  onOpen,
}: {
  chat: ChatContext;
  channel: Channel;
  onOpen: () => void;
}) {
  const dm = isDm(channel);
  const { members, des, people } = useChannelMembers(chat, channel, !dm);
  const rules = useQuery({
    ...rulesQuery(chat.api, chat.co, channel.id),
    retry: false,
    enabled: !dm && des.length > 0,
  });
  if (dm || !members.isSuccess) return null;
  const count = members.data.data.length;
  const faces = [...des, ...people].slice(0, 4);
  const rule = des.length
    ? rules.isSuccess
      ? WAKE_CHIP[rules.data.wake]
      : rules.isError
        ? "rule unavailable"
        : null
    : null;
  const deCount = chat.deDirectory.complete ? des.length : null;
  const name = `Members: ${count}${deCount ? `, ${deCount} ${deCount === 1 ? "digital employee" : "digital employees"}` : ""}${rule ? `; ${rule}` : ""}`;
  return (
    <>
      {chat.deDirectory.failed ? (
        <Button
          variant="ghost"
          size="xs"
          className="text-secondary"
          aria-label="Could not load the digital employees: retry"
          title="Could not load the digital employees: retry"
          onClick={chat.deDirectory.retry}
        >
          Retry
        </Button>
      ) : null}
      {rules.isError && des.length ? (
        <Button
          variant="ghost"
          size="xs"
          className="text-secondary"
          aria-label="Retry loading the agents' rule"
          title="Retry loading the agents' rule"
          onClick={() => void rules.refetch()}
        >
          Retry
        </Button>
      ) : null}
      <Button
        variant="ghost"
        size="xs"
        data-slot="member-stack"
        className="gap-1.5 text-secondary"
        aria-label={name}
        title={name}
        onClick={onOpen}
      >
        <span aria-hidden="true" className="flex items-center -space-x-1">
          {faces.map((m) => (
            <AvatarTile
              key={m.id}
              name={m.kind === "de" ? workerName(m.name) : m.name}
              kind={m.kind === "de" ? "agent" : "person"}
              size="xs"
              data-member-face={m.kind}
              className="ring-2 ring-card"
            />
          ))}
        </span>
        <span data-slot="member-count" aria-hidden="true" className="text-xs tabular-nums">
          {count}
        </span>
        {rule && rule !== WAKE_CHIP.mentions ? (
          <span
            aria-hidden="true"
            data-slot="wake-chip"
            className="rounded-full border border-border px-1.5 text-[11px] leading-4 text-muted-foreground max-sm:hidden"
          >
            {rule}
          </span>
        ) : null}
      </Button>
    </>
  );
}
